/**
 * OKRDashboardContext
 *
 * Context provider that wires all OKR Dashboard hooks together
 * and provides shared state to all child components
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useAppTheme } from '@contexts/ThemeContext';
import { useOKRData, UseOKRDataReturn } from './hooks/useOKRData';
import { useOKRPermissions, UseOKRPermissionsReturn } from './hooks/useOKRPermissions';
import { useExpandedSections, UseExpandedSectionsReturn } from './hooks/useExpandedSections';
import { useOKRDialogs, UseOKRDialogsReturn } from './hooks/useOKRDialogs';
import { useTaskData, UseTaskDataReturn } from './hooks/useTaskData';
import { FilteredObjectives } from './types';
import { filterObjectivesByRole } from './utils/filterUtils';

// =============================================================================
// CONTEXT TYPE
// =============================================================================

export interface OKRDashboardContextValue {
    // Theme
    isDarkMode: boolean;

    // Data Hook
    data: UseOKRDataReturn;

    // Permissions
    permissions: UseOKRPermissionsReturn;

    // Expanded Sections
    sections: UseExpandedSectionsReturn;

    // Dialogs
    dialogs: UseOKRDialogsReturn;

    // Task Data
    tasks: UseTaskDataReturn;

    // Computed Values
    filteredObjectives: FilteredObjectives;
}

// =============================================================================
// CONTEXT
// =============================================================================

const OKRDashboardContext = createContext<OKRDashboardContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

export interface OKRDashboardProviderProps {
    children: React.ReactNode;
}

export const OKRDashboardProvider: React.FC<OKRDashboardProviderProps> = ({ children }) => {
    const { isDarkMode } = useAppTheme();

    // Initialize all hooks
    const data = useOKRData();
    const permissions = useOKRPermissions(data.userContext, data.isLocked);
    const sections = useExpandedSections();
    const dialogsHook = useOKRDialogs();
    const tasks = useTaskData();

    // Compute filtered objectives based on user role
    const filteredObjectives = useMemo(() => {
        return filterObjectivesByRole(
            data.userContext,
            data.orgObjectives,
            data.teamObjectives,
            data.myObjectives
        );
    }, [data.userContext, data.orgObjectives, data.teamObjectives, data.myObjectives]);

    // Combine all values
    const value: OKRDashboardContextValue = useMemo(() => ({
        isDarkMode,
        data,
        permissions,
        sections,
        dialogs: dialogsHook,
        tasks,
        filteredObjectives,
    }), [isDarkMode, data, permissions, sections, dialogsHook, tasks, filteredObjectives]);

    return (
        <OKRDashboardContext.Provider value={value}>
            {children}
        </OKRDashboardContext.Provider>
    );
};

// =============================================================================
// HOOK
// =============================================================================

export const useOKRDashboardContext = (): OKRDashboardContextValue => {
    const context = useContext(OKRDashboardContext);
    if (!context) {
        throw new Error('useOKRDashboardContext must be used within an OKRDashboardProvider');
    }
    return context;
};

// =============================================================================
// CONVENIENCE HOOKS
// =============================================================================

/**
 * Hook to access only the data state
 */
export const useOKRDashboardData = () => {
    const { data } = useOKRDashboardContext();
    return data;
};

/**
 * Hook to access only the permissions
 */
export const useOKRDashboardPermissions = () => {
    const { permissions } = useOKRDashboardContext();
    return permissions;
};

/**
 * Hook to access only the expanded sections state
 */
export const useOKRDashboardSections = () => {
    const { sections } = useOKRDashboardContext();
    return sections;
};

/**
 * Hook to access only the dialogs state
 */
export const useOKRDashboardDialogs = () => {
    const { dialogs } = useOKRDashboardContext();
    return dialogs;
};

/**
 * Hook to access only the task data state
 */
export const useOKRDashboardTasks = () => {
    const { tasks } = useOKRDashboardContext();
    return tasks;
};

/**
 * Hook to access the filtered objectives
 */
export const useOKRFilteredObjectives = () => {
    const { filteredObjectives } = useOKRDashboardContext();
    return filteredObjectives;
};

/**
 * Hook to access the theme
 */
export const useOKRDashboardTheme = () => {
    const { isDarkMode } = useOKRDashboardContext();
    return { isDarkMode };
};

export default OKRDashboardContext;
